import React from 'react';
import * as Sentry from '@sentry/browser';
import { capture } from '@/utils/analytics/capture';
import EmptyLayout from '@/components/Layout/EmptyLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';

class AppErrorBoundary extends React.Component<
  any,
  { eventId?: string; hasError: boolean; error: any; errorInfo: any }
> {
  constructor(props: any) {
    super(props);
    this.state = {
      eventId: undefined,
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  componentDidCatch(error: any, errorInfo: any) {
    this.setState({ errorInfo, error });
    Sentry.withScope((scope) => {
      scope.setTag('AppCrashed', 'true');
      scope.setExtras(errorInfo);
      const eventId = Sentry.captureException(error);
      this.setState({ eventId });
    });
    // Own try/catch: this boundary is the last line of defence and must not
    // itself throw. Only the error's name/type is sent — never the message or
    // stack, which can embed user data; Sentry already owns those above.
    try {
      capture('app_crashed', {
        error_name: error?.name ?? 'Error',
      });
    } catch {
      // never let analytics break the last line of defence
    }
  }

  render() {
    if (this.state.hasError) {
      //render fallback UI
      return (
        <EmptyLayout>
          <div className="w-4/5 md:w-1/3 min-w-80 max-w-lg m-auto">
            <Card
              style={{
                alignItems: 'center',
              }}
            >
              <CardHeader className="text-center">
                <CardTitle>Error</CardTitle>
                <CardDescription>Ups!.. algo salio mal</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col w-full text-center gap-6">
                {import.meta.env.MODE === 'development' && (
                  <>
                    <pre className="min-w-0 whitespace-normal">
                      error: {JSON.stringify(this.state.error)}
                    </pre>
                    <pre className="min-w-0 whitespace-normal">
                      errorInfo: {JSON.stringify(this.state.errorInfo)}
                    </pre>
                    <Button
                      onClick={() =>
                        this.setState({
                          hasError: false,
                          error: null,
                          errorInfo: null,
                        })
                      }
                    >
                      Intentar de nuevo
                    </Button>
                  </>
                )}
                <CardFooter className="flex w-full text-center gap-6 justify-around">
                  <Button
                    onClick={() => (window.location.href = '/')}
                    variant="link"
                  >
                    Salir
                  </Button>
                  <Button
                    onClick={() =>
                      Sentry.showReportDialog({
                        eventId: this.state.eventId,
                        lang: 'es',
                      })
                    }
                    variant="info"
                    className="whitespace-break-spaces"
                  >
                    Enviar Comentarios
                  </Button>
                </CardFooter>
              </CardContent>
            </Card>
          </div>
        </EmptyLayout>
      );
    }

    //when there's not an error, render children untouched
    return this.props.children;
  }
}

export default AppErrorBoundary;
